import * as RadixSwitch from '@radix-ui/react-switch';
import type { SwitchProps as RadixSwitchProps } from '@radix-ui/react-switch';
import { twMerge } from 'tailwind-merge';

import { Loader } from '../../Loader';

type Variants = 'capsule' | 'rect';

type SwitchProps = RadixSwitchProps &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
    readonly size?: 'sm' | 'md' | 'lg';
    readonly variant?: Variants;
    readonly loading?: boolean;
  };

type SizeConfig = {
  readonly root: string;
  readonly thumb: string;
  readonly line: string;
  readonly innerCircle: string;
};

const config: Record<
  Variants,
  {
    root: string;
    thumb: string;
  }
> = {
  capsule: {
    root: twMerge(
      'rounded-full hover:bg-neutral-300',
      'focus-ring',
      'radix-state-checked:bg-blue-500 radix-state-checked:hover:bg-blue-400 radix-state-unchecked:focus:ring-neutral-100',
      'radix-disabled:!bg-neutral-400',
    ),
    thumb: twMerge('left-[2px] top-0.5 rounded-full', 'drop-shadow-md radix-disabled:bg-neutral-200 radix-state-checked:translate-x-full'),
  },
  rect: {
    root: twMerge(
      'h-[30px] w-24 rounded',
      'focus-ring',
      'border border-neutral-300',
      'bg-gradient-to-b from-neutral-200 to-neutral-50',
      'radix-state-checked:from-blue-500 radix-state-checked:to-blue-300',
    ),
    thumb: twMerge(
      'h-7 w-12',
      'bg-gradient-to-b from-neutral-50 to-neutral-200',
      'radix-state-checked:translate-x-[46px] radix-state-checked:rounded-r-[3px] radix-state-checked:border-l radix-state-checked:border-neutral-300',
      'radix-state-unchecked:rounded-l-[3px] radix-state-unchecked:border-r',
    ),
  },
};

const SIZE_CONFIG: { [size: string]: SizeConfig } = {
  sm: {
    root: 'h-[20px] w-[32px]',
    thumb: 'h-[16px] w-[16px] radix-state-checked:-left-[2px]',
    line: 'h-[6px] w-[1px] left-[8px]',
    innerCircle: 'h-[6px] w-[6px] border right-[5px]',
  },
  md: {
    root: 'h-[24px] w-[40px]',
    thumb: 'h-[20px] w-[20px] radix-state-checked:-left-[2px]',
    line: 'h-[6px] w-[1px] left-2.5',
    innerCircle: 'h-[6px] w-[6px] border right-[7px]',
  },
  lg: {
    root: 'h-[32px] w-[53px]',
    thumb: 'h-[28px] w-[28px] radix-state-checked:-left-[5px]',
    line: 'h-2 w-[1.32px] left-[14px]',
    innerCircle: 'h-2 w-2 border-2 right-2.5',
  },
};

function Switch({ variant = 'capsule', size = 'md', loading, disabled, className, ...props }: SwitchProps) {
  const variantStyle = config[variant];
  const sizeStyle = SIZE_CONFIG[size];

  return (
    <RadixSwitch.Root
      {...props}
      disabled={disabled || loading}
      className={twMerge(
        'group relative inline-flex cursor-pointer items-center transition duration-500',
        'bg-neutral-200 ',
        'radix-state-checked:bg-blue-300',
        'dark:border-neutral-700 dark:bg-neutral-700 dark:focus:ring-blue-500',
        'dark:radix-state-checked:bg-blue-300',
        'radix-disabled:cursor-not-allowed',
        variantStyle.root,
        variant === 'capsule' && sizeStyle.root,
        className,
      )}
    >
      {variant === 'capsule' && (
        <>
          {/* line */}
          <span className={twMerge('absolute rounded-full bg-white', disabled && 'bg-neutral-200', sizeStyle.line)} />
          {/* inner circle */}
          <span className={twMerge('absolute rounded-full border-neutral-400', disabled && 'border-neutral-200', sizeStyle.innerCircle)} />
        </>
      )}
      {variant === 'rect' && (
        <div className="flex w-full flex-row text-xs">
          {loading ? (
            <div className="flex w-1/2 justify-center group-radix-state-unchecked:ml-auto">
              <Loader type="ellipsis" className="group-radix-state-checked:fill-white" />
            </div>
          ) : (
            <>
              <p className="flex-1 basis-1 text-white">ON</p>
              <p className="flex-1 basis-1">OFF</p>
            </>
          )}
        </div>
      )}
      <RadixSwitch.Thumb
        className={twMerge(
          'absolute border-neutral-300 bg-white transition-all',
          'radix-state-checked:border-white',
          variantStyle.thumb,
          variant === 'capsule' && sizeStyle.thumb,
        )}
      >
        {variant === 'capsule' && loading && <Loader className="ml-0.5 mt-0.5 stroke-neutral-400" size={16} />}
      </RadixSwitch.Thumb>
    </RadixSwitch.Root>
  );
}

export { Switch };
